import { getOrgIdFromToken, getUserIdFromToken } from "@/api/utils";
import Sentry from "@/integrations/Sentry";
import { garbageCollectMessageDrafts } from "@/utils/messageDraftStorage";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ConnectionNode,
  PlaceholderNode,
  SkillNode,
} from "@cohesive/ui-components";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type MessageDraftIdentityContextValue = {
  identity: string | null;
};

const MessageDraftIdentityContext =
  createContext<MessageDraftIdentityContextValue>({ identity: null });

export function useMessageDraftIdentity(): MessageDraftIdentityContextValue {
  return useContext(MessageDraftIdentityContext);
}

function MessageDraftBootstrap({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [identity, setIdentity] = useState<string | null>(null);
  const gcRanRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setIdentity(null);
      gcRanRef.current = false;
      return;
    }

    let cancelled = false;

    void getAccessTokenSilently()
      .then((token) => {
        if (cancelled) return;
        const nextIdentity = `${getOrgIdFromToken(token)}:${getUserIdFromToken(token)}`;
        setIdentity(nextIdentity);
        if (!gcRanRef.current) {
          garbageCollectMessageDrafts();
          gcRanRef.current = true;
        }
      })
      .catch(() => {
        if (!cancelled) setIdentity(null);
      });

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated]);

  const value = useMemo(() => ({ identity }), [identity]);

  return (
    <MessageDraftIdentityContext.Provider value={value}>
      {children}
    </MessageDraftIdentityContext.Provider>
  );
}

type MessageInputProviderProps = {
  lexicalConfig?: Partial<InitialConfigType>;
  children: React.ReactNode;
};

export const MessageInputProvider = (props: MessageInputProviderProps) => {
  const config = useMemo(() => {
    const c: InitialConfigType = {
      namespace: "MessageInput",
      nodes: [SkillNode, ConnectionNode, PlaceholderNode],
      onError: (e) => Sentry.captureException(e),
      ...props.lexicalConfig,
    };
    return c;
  }, [props.lexicalConfig]);

  return (
    <LexicalComposer initialConfig={config}>
      <MessageDraftBootstrap>{props.children}</MessageDraftBootstrap>
    </LexicalComposer>
  );
};

export function withMessageInputContext<P extends object>(
  Component: React.ComponentType<P>,
  props?: Omit<MessageInputProviderProps, "children">,
): React.FC<P> {
  return function WrappedWithMessageInputContext(p: P) {
    return (
      <MessageInputProvider {...props}>
        <Component {...p} />
      </MessageInputProvider>
    );
  };
}
